/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { OnInit, Component, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import SignaturePad from 'signature_pad';
import {
	AppVariables,
	ErrorCodes,
	PlatformName,
	PlatformProvider,
	ScreenOrientationService,
	ToastDuration,
	ToastService
} from '@shared';
import { LoggingService } from '@shared/logging';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-signature',
	templateUrl: './signature.component.html',
	styleUrls: ['./signature.component.scss']
})
export class SignatureComponent implements OnInit, OnDestroy {
	@ViewChild('canvas', { static: true }) private signaturePadElement;
	@ViewChild('content') private content;
	@ViewChild('fileInput') private fileInput: ElementRef;

	public signaturePad: SignaturePad;
	public dataUrl: string = '';
	public isImageUploaded = false;
	public isSignatureDrawn = false;
	public canvasHeight: number;
	public canvasWidth: number;
	public isOrientationLandscape: boolean;

	private subscriptions: Subscription[] = [];
	private MAXIMUM_PHOTO_SIZE_ALLOWED = 5_000_000;

	public constructor(
		private modalCtrl: ModalController,
		private platformProvider: PlatformProvider,
		private toastService: ToastService,
		private loggingService: LoggingService,
		private translateService: TranslateService,
		private screenOrientationService: ScreenOrientationService
	) {
		this.canvasHeight = screen.height * 0.6;
		this.canvasWidth = screen.width;
	}

	public ngOnInit(): void {
		this.handleScreenOrientation();
		this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement);
		this.signaturePad.clear();
		
		console.log('data Url', this);
	}

	public clear(): void {
		this.signaturePad.clear();
		this.dataUrl = '';
		this.isSignatureDrawn = false;
	}

	public close(): void {
		this.modalCtrl.dismiss(undefined);
	}

	public save(): void {
		this.modalCtrl.dismiss(this.dataUrl);
	}

	public saveSignature(): void {
		if(!this.signaturePad.isEmpty()){
		this.dataUrl = this.formatBase64Photo(this.signaturePad.toDataURL());
		console.log('save signature', this);
		}
	}

	public scrollToBottom(): void {
		this.content.scrollToBottom(AppVariables.FORM_SCROLL_UP_DURATION);
	}

	public removeUploadedPhoto(event: any): void {
		this.dataUrl = '';
		this.isImageUploaded = false;
		event.stopPropagation();
	}

	public trackSignature(): void {
		this.isSignatureDrawn = true;
	}

	public async openGallery(): Promise<void> {
		if (this.isImageUploaded) {
			return;
		}

		const hasPermissions =
			this.platformProvider.getPlatform() === PlatformName.WEB
				? { photos: true }
				: await Camera.requestPermissions();

		if (!hasPermissions.photos) {
			this.toastService.presentError(
				this.translateService.instant('signature.errorMessages.noGalleryPermission'),
				ToastDuration.Slow
			);
			return;
		}

		if(this.platformProvider.getPlatform() === PlatformName.WEB){
			if(this.dataUrl){
				this.save();
				// console.log('save web', this);
			} else {
				// console.log('save canvas', this);
				this.fileInput.nativeElement.click();
			}
		}

		try {
			const photo = await Camera.getPhoto({
				quality: 40,
				resultType: CameraResultType.DataUrl,
				source: CameraSource.Prompt
			});

			this.dataUrl = this.formatBase64Photo(photo.dataUrl);
			const photoSize = 4 * Math.ceil(this.dataUrl.length / 3);
			if (photoSize > this.MAXIMUM_PHOTO_SIZE_ALLOWED) {
				throw new Error('Maximum size exceeded');
			}

			this.isImageUploaded = true;
			this.toastService.presentSuccess(
				this.translateService.instant('signature.successfulUpload'),
				ToastDuration.Fast
			);
		} catch (error) {
			this.isImageUploaded = false;
			this.loggingService.error(ErrorCodes.GalleryPhotoUploadError, error);
			this.toastService.presentError(error, ToastDuration.Slow);
		}
	}

	public handleFileSelect(event: any) {
        // Handle the selected file(s) here
        const files = event.target.files;
		if (files && files.length > 0) {
            const file = files[0]; 
            const reader = new FileReader();

            reader.onload = () => {
                const dataUrl = reader.result as string;

                this.dataUrl = this.formatBase64Photo(dataUrl);
				const photoSize = 4 * Math.ceil(this.dataUrl.length / 3);
				if (photoSize > this.MAXIMUM_PHOTO_SIZE_ALLOWED) {
				throw new Error('Maximum size exceeded');
				}
				
				//console.log('data', this.dataUrl);
				this.isImageUploaded = true;
				
				this.toastService.presentSuccess(
				this.translateService.instant('signature.successfulUpload'),
				ToastDuration.Fast);
            };
			
			reader.readAsDataURL(file);
        }
    }

	public ngOnDestroy(): void {
		this.subscriptions.forEach((subscription) => subscription.unsubscribe());
	}

	private handleScreenOrientation(): void {
		this.subscriptions.push(
			this.screenOrientationService.isOrientationLandscape$.subscribe(
				(isOrientationLandscape: boolean) => (this.isOrientationLandscape = isOrientationLandscape)
			)
		);
	}

	private formatBase64Photo(base64String: string): string {
		return base64String.split(',')[1];
	}
}
